import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import { CbcStatelessPushService } from './cbc-stateless-push.service';

@Component({
    selector: 'gtw-cbc-stateless-push',
    templateUrl: './cbc-stateless-push.component.html',
    styleUrls: ['./cbc-stateless-push.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CbcStatelessPushComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  confirmConfig: ConfirmationModalConfig = {
    title: 'Confirm Refresh',
    message: 'Are you sure you want to push the data to table1?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};
  pushLoading = false;

  constructor(
    private statelessPushService: CbcStatelessPushService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  onConfirm(): void {
    this.pushLoading = true;
    this.confirmConfig = { ...this.confirmConfig, loading: true };
    this.cdr.detectChanges();

    this.gtwSnackbarService.openSnackBar('Data push is In-Progress.', 'gtw-snackbar--green');

    this.statelessPushService
      .pushStatelessData(this.baseUrl, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.pushLoading = false;
          this.confirmConfig = { ...this.confirmConfig, loading: false };
          if (response?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(
              'Data is successfully pushed to table1.',
              'gtw-snackbar--green'
            );
            this.emitCloseAndNotify();
          } else if (response?.errorMessage) {
            this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
            this.cancel();
          } else {
            this.cancel();
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.pushLoading = false;
          this.confirmConfig = { ...this.confirmConfig, loading: false };
          this.gtwSnackbarService.openSnackBar(
            err?.message || 'Error pushing data, please try again!',
            'gtw-snackbar--red'
          );
          this.cancel();
          this.cdr.detectChanges();
        },
      });
  }

  onCancel(): void {
    this.cancel();
  }

  private emitCloseAndNotify(): void {
    // Match original AngularJS factory which broadcasts dataFilters:refreshGrid
    // with refresh:false (no full grid reload, but still notifies subscribers).
    this.dispatchUpdate('refresh');
  }

  private cancel(): void {
    this.dispatchUpdate('cancel');
  }

  private dispatchUpdate(detail: 'refresh' | 'cancel'): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail,
      bubbles: true,
    });
    document
      .querySelector('gtw-cbc-stateless-push-ce')
      ?.dispatchEvent(updateEvent);
  }
}
